/**
 * 3D neighbourhood tools that SuperSplat doesn't have:
 *  - grow / shrink the selection by a world-space radius,
 *  - "3D magic wand": flood from seed splats through spatial neighbours,
 *    optionally limited to similar colours,
 *  - automatic floater detection: small disconnected clusters and isolated
 *    splats far from the main body of the capture.
 *
 * All of them run on a uniform hash grid over world-space centers.
 */
import type { SplatEditModel } from '../SplatEditModel';
import { SplatState } from '../SplatState';
import { computeDisplayColors } from './props';

export interface SpatialGrid {
    cell: number;
    /** Distinct cell keys (exact integers) and, per cell, the [start, end) range into `items`. */
    keys: Float64Array;
    starts: Uint32Array;
    items: Uint32Array;
    minX: number; minY: number; minZ: number;
    dimX: number; dimY: number;
    /** Open-addressing table: slot → cell index (-1 = empty). */
    table: Int32Array;
    mask: number;
}

/** Spread integer cell coordinates over the hash table. */
function hashCell(cx: number, cy: number, cz: number, mask: number): number {
    let h = Math.imul(cx, 0x8da6b343) ^ Math.imul(cy, 0xd8163841) ^ Math.imul(cz, 0xcb1ab31f);
    h ^= h >>> 15;
    return h & mask;
}

/**
 * Build a hashed uniform grid over the included centers. Neighbour queries
 * need radius ≤ cell. Building is a counting sort (O(n)); lookups are O(1).
 */
export function buildGrid(centers: Float32Array, include: Uint8Array, cell: number): SpatialGrid {
    const n = include.length;
    let minX = Infinity, minY = Infinity, minZ = Infinity, maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    let count = 0;
    for (let i = 0; i < n; i++) {
        if (!include[i]) continue;
        const x = centers[i * 3], y = centers[i * 3 + 1], z = centers[i * 3 + 2];
        if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) { include[i] = 0; continue; }
        if (x < minX) minX = x; if (x > maxX) maxX = x;
        if (y < minY) minY = y; if (y > maxY) maxY = y;
        if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
        count++;
    }
    if (count === 0) {
        return {
            cell, keys: new Float64Array(0), starts: new Uint32Array(1), items: new Uint32Array(0),
            minX: 0, minY: 0, minZ: 0, dimX: 1, dimY: 1, table: new Int32Array(1).fill(-1), mask: 0,
        };
    }
    // keys must stay exact in a double: keep each axis under 2^17 cells. A coarser
    // cell is still correct for neighbour queries (they need radius <= cell).
    const AXIS_LIMIT = 1 << 17;
    // A zero (or negative / NaN) cell, e.g. growSelection(model, 0), would never
    // satisfy the loop below (0 * 2 = 0); start from the finest cell the axis limit allows.
    if (!(cell > 0)) cell = Math.max(maxX - minX, maxY - minY, maxZ - minZ) / (AXIS_LIMIT - 2) || 1;
    while ((maxX - minX) / cell >= AXIS_LIMIT - 1 || (maxY - minY) / cell >= AXIS_LIMIT - 1 || (maxZ - minZ) / cell >= AXIS_LIMIT - 1) {
        cell *= 2;
    }
    const dimX = Math.floor((maxX - minX) / cell) + 1;
    const dimY = Math.floor((maxY - minY) / cell) + 1;

    let size = 1;
    while (size < count * 2) size <<= 1;
    const mask = size - 1;
    const table = new Int32Array(size).fill(-1);
    const keyList: number[] = [];
    const cellOf = new Int32Array(count);
    const counts: number[] = [];
    const itemIdx = new Uint32Array(count);
    let o = 0;
    for (let i = 0; i < n; i++) {
        if (!include[i]) continue;
        const cx = Math.floor((centers[i * 3] - minX) / cell);
        const cy = Math.floor((centers[i * 3 + 1] - minY) / cell);
        const cz = Math.floor((centers[i * 3 + 2] - minZ) / cell);
        const key = (cz * dimY + cy) * dimX + cx;
        let slot = hashCell(cx, cy, cz, mask);
        let c: number;
        for (;;) {
            c = table[slot];
            if (c < 0) {
                c = keyList.length;
                keyList.push(key);
                counts.push(0);
                table[slot] = c;
                break;
            }
            if (keyList[c] === key) break;
            slot = (slot + 1) & mask;
        }
        counts[c]++;
        cellOf[o] = c;
        itemIdx[o] = i;
        o++;
    }
    const cells = keyList.length;
    const starts = new Uint32Array(cells + 1);
    for (let c = 0; c < cells; c++) starts[c + 1] = starts[c] + counts[c];
    const fill = starts.slice(0, cells);
    const items = new Uint32Array(count);
    for (let k = 0; k < count; k++) items[fill[cellOf[k]]++] = itemIdx[k];
    return { cell, keys: Float64Array.from(keyList), starts, items, minX, minY, minZ, dimX, dimY, table, mask };
}

function findCell(grid: SpatialGrid, cx: number, cy: number, cz: number): number {
    const key = (cz * grid.dimY + cy) * grid.dimX + cx;
    let slot = hashCell(cx, cy, cz, grid.mask);
    const { table, keys, mask } = grid;
    for (;;) {
        const c = table[slot];
        if (c < 0) return -1;
        if (keys[c] === key) return c;
        slot = (slot + 1) & mask;
    }
}

/**
 * Call fn(j) for every grid item within `radius` of point p. Cheapest when
 * radius ≤ grid.cell (a 3×3×3 cell scan); larger radii scan ⌈radius / cell⌉
 * cells each way.
 */
export function forEachNeighbour(
    grid: SpatialGrid, centers: Float32Array, px: number, py: number, pz: number, radius: number,
    fn: (j: number) => boolean | void,
): void {
    if (grid.items.length === 0 || !(radius >= 0)) return;
    const r2 = radius * radius;
    const cx = Math.floor((px - grid.minX) / grid.cell);
    const cy = Math.floor((py - grid.minY) / grid.cell);
    const cz = Math.floor((pz - grid.minZ) / grid.cell);
    const k = Math.max(1, Math.ceil(radius / grid.cell));
    const { starts, items } = grid;
    for (let dz = -k; dz <= k; dz++) {
        const z = cz + dz;
        if (z < 0) continue;
        for (let dy = -k; dy <= k; dy++) {
            const y = cy + dy;
            if (y < 0 || y >= grid.dimY) continue;
            for (let dx = -k; dx <= k; dx++) {
                const x = cx + dx;
                if (x < 0 || x >= grid.dimX) continue;
                const c = findCell(grid, x, y, z);
                if (c < 0) continue;
                for (let k = starts[c], e = starts[c + 1]; k < e; k++) {
                    const j = items[k];
                    const ex = centers[j * 3] - px, ey = centers[j * 3 + 1] - py, ez = centers[j * 3 + 2] - pz;
                    if (ex * ex + ey * ey + ez * ez <= r2) {
                        if (fn(j) === false) return;
                    }
                }
            }
        }
    }
}

/**
 * Typical spacing between neighbouring splats: median nearest-neighbour
 * distance over a sample. Used for default radii (3D magic wand, floaters,
 * grow / shrink, clone stamp).
 *
 * Real scans have stray splats hundreds of metres out. Sizing the search grid
 * from min/max bounds made its cells so large that every sample scanned most
 * of the scene (78 s on a 2M-splat room with 3% far floaters, freezing the
 * editor). The grid is sized from the 2nd-98th percentile bounds instead,
 * shrinks while the samples land in crowded cells, and each query has a hard
 * work cap, so no input can stall it.
 */
export function estimateSpacing(model: SplatEditModel, samples = 2000): number {
    return runSteps(estimateSpacingSteps(model, samples));
}

/** Run resumable steps (selectConnectedSteps, estimateSpacingSteps, analyseFloatersSteps) to the end. */
export function runSteps<T>(steps: Generator<void, T, void>): T {
    let r = steps.next();
    while (!r.done) r = steps.next();
    return r.value;
}

/**
 * estimateSpacing as resumable steps: yields between grid builds and every
 * few hundred probes, so a caller can hand the thread back on big scans.
 */
export function* estimateSpacingSteps(model: SplatEditModel, samples = 2000): Generator<void, number, void> {
    const centers = model.getWorldCenters();
    const n = model.numSplats;
    const live = new Uint8Array(n);
    let liveCount = 0;
    for (let i = 0; i < n; i++) {
        if (model.state[i] & SplatState.deleted) continue;
        if (!Number.isFinite(centers[i * 3]) || !Number.isFinite(centers[i * 3 + 1]) || !Number.isFinite(centers[i * 3 + 2])) continue;
        live[i] = 1;
        liveCount++;
    }
    if (liveCount < 2) return 1;

    const bounds = percentileBounds(centers, live, liveCount, 0.02);
    const inside = new Uint8Array(n);
    let insideCount = 0;
    for (let i = 0; i < n; i++) {
        if (!live[i]) continue;
        const x = centers[i * 3], y = centers[i * 3 + 1], z = centers[i * 3 + 2];
        if (x < bounds[0] || x > bounds[3] || y < bounds[1] || y > bounds[4] || z < bounds[2] || z > bounds[5]) continue;
        inside[i] = 1;
        insideCount++;
    }
    if (insideCount < 2) { inside.set(live); insideCount = liveCount; }
    yield;

    const probes: number[] = [];
    const step = Math.max(1, Math.floor(n / samples));
    for (let i = 0; i < n && probes.length < samples; i += step) if (inside[i]) probes.push(i);
    if (probes.length === 0) return 1;

    // First guess from the density inside the bounds, by the capture's dimension:
    // a flat or linear capture has ~zero volume and would start far too small.
    const ext = [bounds[3] - bounds[0], bounds[4] - bounds[1], bounds[5] - bounds[2]].sort((a, b) => b - a);
    let cell = ext[2] > ext[0] * 1e-3
        ? Math.cbrt((ext[0] * ext[1] * ext[2]) / insideCount) * 2
        : ext[1] > ext[0] * 1e-3
            ? Math.sqrt((ext[0] * ext[1]) / insideCount) * 2
            : (ext[0] / insideCount) * 2;
    if (!(cell > 0)) cell = 1e-6;

    const QUERY_BUDGET = 20000;
    let grew = false;
    for (let attempt = 0; attempt < 24; attempt++) {
        const grid = buildGrid(centers, inside.slice(), cell);
        yield;
        // Too coarse: the probes sit in crowded cells, and each query would scan them all.
        if (!grew && grid.cell === cell) {
            let occupancy = 0;
            for (const i of probes) {
                const c = cellOfPoint(grid, centers, i);
                if (c >= 0) occupancy += grid.starts[c + 1] - grid.starts[c];
            }
            occupancy /= probes.length;
            if (occupancy > 64) {
                cell /= Math.min(16, Math.max(2, Math.sqrt(occupancy / 16)));
                continue;
            }
        }
        const dists: number[] = [];
        let probed = 0;
        for (const i of probes) {
            if (++probed % 250 === 0) yield;
            const px = centers[i * 3], py = centers[i * 3 + 1], pz = centers[i * 3 + 2];
            let best = Infinity;
            let budget = QUERY_BUDGET;
            forEachNeighbour(grid, centers, px, py, pz, grid.cell, (j) => {
                if (j === i) return;
                const ex = centers[j * 3] - px, ey = centers[j * 3 + 1] - py, ez = centers[j * 3 + 2] - pz;
                const d2 = ex * ex + ey * ey + ez * ez;
                if (d2 < best) best = d2;
                if (--budget <= 0) return false;
            });
            if (best < Infinity) dists.push(Math.sqrt(best));
        }
        if (dists.length >= Math.min(50, probes.length / 2)) {
            dists.sort((a, b) => a - b);
            const median = dists[dists.length >> 1];
            return median > 0 ? median : grid.cell / 4;
        }
        // Too fine: most probes have no neighbour within a cell.
        cell = grid.cell * 4;
        grew = true;
    }
    return cell;
}

/** [minX, minY, minZ, maxX, maxY, maxZ] between the `q` and `1 - q` quantiles of a sample of the included centers. */
function percentileBounds(centers: Float32Array, include: Uint8Array, count: number, q: number): number[] {
    const n = include.length;
    const want = Math.min(count, 32768);
    const step = Math.max(1, Math.floor(count / want));
    const xs = new Float32Array(want), ys = new Float32Array(want), zs = new Float32Array(want);
    let m = 0;
    for (let i = 0, seen = 0; i < n && m < want; i++) {
        if (!include[i]) continue;
        if (seen++ % step) continue;
        xs[m] = centers[i * 3]; ys[m] = centers[i * 3 + 1]; zs[m] = centers[i * 3 + 2];
        m++;
    }
    const lo = Math.floor((m - 1) * q), hi = Math.ceil((m - 1) * (1 - q));
    const out = [0, 0, 0, 0, 0, 0];
    [xs, ys, zs].forEach((axis, a) => {
        const sorted = axis.subarray(0, m).sort();
        out[a] = sorted[lo];
        out[a + 3] = sorted[hi];
    });
    return out;
}

/**
 * Robust center of the selected splats (or of every live splat when
 * `selectedOnly` is false or nothing is selected): the per-axis median of the
 * world centers, over at most ~65k of them. The transform pivot sits here.
 * Screen selections take every splat under the outline at any depth, so a
 * bounds center is dragged off by a few strays far behind; the median isn't.
 */
export function robustCenter(model: SplatEditModel, selectedOnly: boolean): [number, number, number] | null {
    const c = model.getWorldCenters();
    const state = model.state;
    const n = model.numSplats;
    const useSel = selectedOnly && model.counts.selected > 0;
    const count = useSel ? model.counts.selected : n;
    const step = Math.max(1, Math.floor(count / 65536));
    const xs: number[] = [], ys: number[] = [], zs: number[] = [];
    for (let i = 0, seen = 0; i < n; i++) {
        const st = state[i];
        if (st & SplatState.deleted) continue;
        if (useSel && !(st & SplatState.selected)) continue;
        if (seen++ % step) continue;
        const x = c[i * 3], y = c[i * 3 + 1], z = c[i * 3 + 2];
        if (!Number.isFinite(x + y + z)) continue;
        xs.push(x); ys.push(y); zs.push(z);
    }
    if (!xs.length) return null;
    const median = (v: number[]) => { v.sort((a, b) => a - b); return v[v.length >> 1]; };
    return [median(xs), median(ys), median(zs)];
}

function selectableMask(model: SplatEditModel): Uint8Array {
    const n = model.numSplats;
    const m = new Uint8Array(n);
    for (let i = 0; i < n; i++) if (!(model.state[i] & (SplatState.hidden | SplatState.deleted))) m[i] = 1;
    return m;
}

/** Mask of every selectable splat within `radius` of a selected splat (selection included). */
export function growSelection(model: SplatEditModel, radius: number): Uint8Array {
    const centers = model.getWorldCenters();
    const n = model.numSplats;
    const include = selectableMask(model);
    const grid = buildGrid(centers, include.slice(), radius);
    const out = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
        if ((model.state[i] & 7) !== SplatState.selected) continue;
        out[i] = 1;
        forEachNeighbour(grid, centers, centers[i * 3], centers[i * 3 + 1], centers[i * 3 + 2], radius, (j) => { out[j] = 1; });
    }
    return out;
}

/** Mask of selected splats that have no unselected selectable splat within `radius` (erosion). */
export function shrinkSelection(model: SplatEditModel, radius: number): Uint8Array {
    const centers = model.getWorldCenters();
    const n = model.numSplats;
    const unselected = new Uint8Array(n);
    for (let i = 0; i < n; i++) if ((model.state[i] & 7) === 0) unselected[i] = 1;
    const grid = buildGrid(centers, unselected, radius);
    const out = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
        if ((model.state[i] & 7) !== SplatState.selected) continue;
        let touches = false;
        forEachNeighbour(grid, centers, centers[i * 3], centers[i * 3 + 1], centers[i * 3 + 2], radius, () => { touches = true; return false; });
        if (!touches) out[i] = 1;
    }
    return out;
}

/**
 * 3D magic wand: flood from `seeds` through splats within `radius` of each
 * other. With `colorTolerance` (0..1) the flood only crosses to splats whose
 * display colour is within tolerance of the seed average on every channel.
 */
export function selectConnected(
    model: SplatEditModel, seeds: ArrayLike<number>, radius: number,
    options: ConnectedOptions = {},
): Uint8Array {
    return runSteps(selectConnectedSteps(model, seeds, radius, options));
}

export interface ConnectedOptions {
    colorTolerance?: number;
    cameraPos?: { x: number; y: number; z: number };
    maxSplats?: number;
}

/**
 * selectConnected as resumable steps: yields after every `sliceSplats`
 * flooded splats so a caller can hand the thread back between slices (a
 * flood across a whole multi-million-splat scan takes seconds). Returns the mask.
 */
export function* selectConnectedSteps(
    model: SplatEditModel, seeds: ArrayLike<number>, radius: number,
    options: ConnectedOptions = {}, sliceSplats = 100_000,
): Generator<void, Uint8Array, void> {
    const centers = model.getWorldCenters();
    const n = model.numSplats;
    const include = selectableMask(model);
    const grid = buildGrid(centers, include.slice(), radius);
    const out = new Uint8Array(n);
    const tol = options.colorTolerance;

    // Exact per-splat flood (a hidden splat must be able to cut a thin bridge).
    const colors = tol !== undefined ? computeDisplayColors(model, options.cameraPos) : null;
    let rr = 0, gg = 0, bb = 0, seedCount = 0;
    const queue: number[] = [];
    for (let s = 0; s < seeds.length; s++) {
        const i = seeds[s];
        if (i < 0 || i >= n || !include[i] || out[i]) continue;
        out[i] = 1;
        queue.push(i);
        if (colors) { rr += colors[i * 3]; gg += colors[i * 3 + 1]; bb += colors[i * 3 + 2]; seedCount++; }
    }
    if (seedCount) { rr /= seedCount; gg /= seedCount; bb /= seedCount; }
    const max = options.maxSplats ?? Infinity;
    let visited = queue.length;
    const r2 = radius * radius;
    const { starts, items } = grid;
    let sinceYield = 0;
    while (queue.length && visited < max) {
        if (++sinceYield >= sliceSplats) { sinceYield = 0; yield; }
        const i = queue.pop()!;
        const px = centers[i * 3], py = centers[i * 3 + 1], pz = centers[i * 3 + 2];
        const [cx, cy, cz] = pointCoords(grid, px, py, pz);
        for (let dz = -1; dz <= 1; dz++) for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
            const x = cx + dx, y = cy + dy, z = cz + dz;
            if (x < 0 || y < 0 || z < 0 || x >= grid.dimX || y >= grid.dimY) continue;
            const c = findCell(grid, x, y, z);
            if (c < 0) continue;
            for (let k = starts[c], e = starts[c + 1]; k < e; k++) {
                const j = items[k];
                if (out[j]) continue;
                const ex = centers[j * 3] - px, ey = centers[j * 3 + 1] - py, ez = centers[j * 3 + 2] - pz;
                if (ex * ex + ey * ey + ez * ez > r2) continue;
                if (colors && tol !== undefined && (Math.abs(colors[j * 3] - rr) > tol || Math.abs(colors[j * 3 + 1] - gg) > tol || Math.abs(colors[j * 3 + 2] - bb) > tol)) continue;
                out[j] = 1;
                queue.push(j);
                visited++;
            }
        }
    }
    return out;
}

/** The 13 neighbour offsets (dx, dy, dz) that come after a cell: each neighbouring pair once. */
const FORWARD: number[] = [];
for (let dz = -1; dz <= 1; dz++) for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
    if (dz > 0 || (dz === 0 && (dy > 0 || (dy === 0 && dx > 0)))) FORWARD.push(dx, dy, dz);
}

function pointCoords(grid: SpatialGrid, x: number, y: number, z: number): [number, number, number] {
    return [Math.floor((x - grid.minX) / grid.cell), Math.floor((y - grid.minY) / grid.cell), Math.floor((z - grid.minZ) / grid.cell)];
}

function cellOfPoint(grid: SpatialGrid, centers: Float32Array, i: number): number {
    const [x, y, z] = pointCoords(grid, centers[i * 3], centers[i * 3 + 1], centers[i * 3 + 2]);
    return findCell(grid, x, y, z);
}

function cellCoords(grid: SpatialGrid, c: number): [number, number, number] {
    const key = grid.keys[c];
    const cx = key % grid.dimX;
    const rest = (key - cx) / grid.dimX;
    const cy = rest % grid.dimY;
    const cz = (rest - cy) / grid.dimY;
    return [cx, cy, cz];
}

/**
 * Coarser linking levels a floater analysis keeps: level k joins splats across
 * gaps 2^k times the base radius, so level 8 (768 × typical spacing at the
 * default radius) only leaves clusters far from everything else.
 */
export const FLOATER_LEVELS = 8;

/** Strength per linking level at the low end of the slider (one 5% slider step each). */
const STRENGTH_PER_LEVEL = 0.05;
const BASE_STRENGTH = FLOATER_LEVELS * STRENGTH_PER_LEVEL;

/**
 * Floater detection settings for a single 0..1 strength (the panel's slider).
 * Below 0.4 only small clusters cut off from the rest by a gap are floaters,
 * and the gap narrows as strength rises: 0 links across 2^8 × the base radius,
 * each 0.05 halves that. Real scans have sparse but genuine regions (ceilings,
 * distant background) that fragment at the base radius, so linking wider is
 * what keeps the low end from flagging them. From 0.4 up it links at the base
 * radius and treats larger stray clusters and sparser splats as floaters.
 */
export function floaterOptionsForStrength(strength: number): Required<Pick<FloaterOptions, 'minClusterFraction' | 'minNeighbours' | 'level'>> {
    const s = Math.max(0, Math.min(1, strength));
    // the epsilon keeps values like 0.39999999999999997 on their own slider step
    const step = Math.floor(s / STRENGTH_PER_LEVEL + 1e-6);
    if (step < FLOATER_LEVELS) return { minClusterFraction: 0.001, minNeighbours: 0, level: FLOATER_LEVELS - step };
    const t = Math.max(0, (s - BASE_STRENGTH) / (1 - BASE_STRENGTH));
    return { minClusterFraction: 0.001 + t * 0.05, minNeighbours: 1 + Math.round(t * 4), level: 0 };
}

export interface FloaterOptions {
    /** Linking radius; defaults to 3 × typical spacing. */
    radius?: number;
    /** Clusters smaller than this fraction of the largest cluster (at the base radius) are floaters. Default 0.01. */
    minClusterFraction?: number;
    /** Splats with fewer neighbours than this nearby (27-cell neighbourhood) are floaters. Default 2. */
    minNeighbours?: number;
    /**
     * Linking level, 0..FLOATER_LEVELS: clusters are joined across gaps up to
     * 2^level × radius, so a higher level only flags clusters far from
     * everything else. Default 0.
     */
    level?: number;
}

/**
 * Detect floaters at voxel granularity (cell = radius): occupied cells are
 * joined into 26-connected components with union-find; splats in components
 * smaller than `minClusterFraction` of the largest one (with cells 2^`level`
 * times larger, so across wider gaps), or with fewer than `minNeighbours`
 * other splats in their 3×3×3 cell neighbourhood, are floaters. Linear in the
 * number of splats, so it runs on 20M-splat scans.
 */
export function detectFloaters(model: SplatEditModel, options: FloaterOptions = {}): { mask: Uint8Array; radius: number; clusters: number } {
    const analysis = analyseFloaters(model, options.radius ?? estimateSpacing(model) * 3);
    return floaterMask(analysis, options);
}

/**
 * The expensive half of floater detection (grid + connected components),
 * independent of the thresholds, so a strength slider can re-threshold it
 * instantly with floaterMask().
 */
export interface FloaterAnalysis {
    numSplats: number;
    radius: number;
    grid: SpatialGrid;
    /** Per cell: splats in its connected component. */
    componentSize: Uint32Array;
    /** Per cell: splats in its 3×3×3 neighbourhood (itself included). */
    neighbourhood: Uint32Array;
    largest: number;
    clusters: number;
    /** Coarser linking levels: levels[k - 1] is level k (cells 2^k times the grid's). */
    levels: FloaterLevel[];
}

/** Connected components at one coarser linking level. */
export interface FloaterLevel {
    /** Per cell of the level below (the grid itself for level 1): its cell at this level. */
    parent: Uint32Array;
    /** Per cell: splats in its connected component. */
    componentSize: Uint32Array;
    clusters: number;
}

export function analyseFloaters(model: SplatEditModel, radius: number): FloaterAnalysis {
    return runSteps(analyseFloatersSteps(model, radius));
}

/** analyseFloaters as resumable steps: yields after the grid build, every 100k cells of each pass and between levels. */
export function* analyseFloatersSteps(model: SplatEditModel, radius: number): Generator<void, FloaterAnalysis, void> {
    const centers = model.getWorldCenters();
    const n = model.numSplats;
    const include = selectableMask(model);
    const grid = buildGrid(centers, include.slice(), radius);
    yield;
    const cells = grid.keys.length;
    if (cells === 0) {
        return { numSplats: n, radius, grid, componentSize: new Uint32Array(0), neighbourhood: new Uint32Array(0), largest: 0, clusters: 0, levels: [] };
    }
    const { starts } = grid;
    const coords = new Int32Array(cells * 3);
    const counts = new Uint32Array(cells);
    let dimZ = 1;
    for (let c = 0; c < cells; c++) {
        const [cx, cy, cz] = cellCoords(grid, c);
        coords[c * 3] = cx; coords[c * 3 + 1] = cy; coords[c * 3 + 2] = cz;
        if (cz >= dimZ) dimZ = cz + 1;
        counts[c] = starts[c + 1] - starts[c];
    }
    let below: LevelCells = { coords, counts, dimX: grid.dimX, dimY: grid.dimY, dimZ, order: sortCells(coords, grid.dimX, grid.dimY, dimZ) };
    yield;
    const neighbourhood = counts.slice();
    const { componentSize, largest, clusters } = yield* cellComponents(below, neighbourhood);

    // Coarser levels from the cells of the level below (not the splats), so
    // each costs a fraction of the base level.
    const levels: FloaterLevel[] = [];
    for (let k = 1; k <= FLOATER_LEVELS; k++) {
        yield;
        const { parent, cells: coarse } = coarsenCells(below);
        yield;
        const level = yield* cellComponents(coarse);
        levels.push({ parent, componentSize: level.componentSize, clusters: level.clusters });
        below = coarse;
    }
    return { numSplats: n, radius, grid, componentSize, neighbourhood, largest, clusters, levels };
}

/** Occupied cells of one linking level: integer coordinates, splat counts and the cells in key order. */
interface LevelCells {
    coords: Int32Array;
    counts: Uint32Array;
    dimX: number; dimY: number; dimZ: number;
    /** Cell indices sorted by key, (z · dimY + y) · dimX + x. */
    order: Uint32Array;
}

/** Indices of the cells in key order: stable counting sorts by x, then y, then z. */
function sortCells(coords: Int32Array, dimX: number, dimY: number, dimZ: number): Uint32Array {
    const n = coords.length / 3;
    let order = new Uint32Array(n);
    for (let i = 0; i < n; i++) order[i] = i;
    let out = new Uint32Array(n);
    const dims = [dimX, dimY, dimZ];
    for (let axis = 0; axis < 3; axis++) {
        const count = new Uint32Array(dims[axis] + 1);
        for (let i = 0; i < n; i++) count[coords[order[i] * 3 + axis] + 1]++;
        for (let v = 1; v < count.length; v++) count[v] += count[v - 1];
        for (let i = 0; i < n; i++) { const c = order[i]; out[count[coords[c * 3 + axis]]++] = c; }
        const t = order; order = out; out = t;
    }
    return order;
}

/** The cells of the next coarser level (twice the cell size) and, per cell below, its coarse cell. */
function coarsenCells(below: LevelCells): { parent: Uint32Array; cells: LevelCells } {
    const n = below.counts.length;
    const dimX = (below.dimX + 1) >> 1, dimY = (below.dimY + 1) >> 1, dimZ = (below.dimZ + 1) >> 1;
    const halved = new Int32Array(n * 3);
    for (let i = 0; i < n * 3; i++) halved[i] = below.coords[i] >> 1;
    // walk the cells below in coarse key order: each new key is the next coarse cell
    const byKey = sortCells(halved, dimX, dimY, dimZ);
    const parent = new Uint32Array(n);
    const coords = new Int32Array(n * 3);
    const counts = new Uint32Array(n);
    let cells = 0, last = -1;
    for (let i = 0; i < n; i++) {
        const p = byKey[i];
        const x = halved[p * 3], y = halved[p * 3 + 1], z = halved[p * 3 + 2];
        const key = (z * dimY + y) * dimX + x;
        if (key !== last) {
            coords[cells * 3] = x; coords[cells * 3 + 1] = y; coords[cells * 3 + 2] = z;
            cells++;
            last = key;
        }
        counts[cells - 1] += below.counts[p];
        parent[p] = cells - 1;
    }
    const order = new Uint32Array(cells);
    for (let c = 0; c < cells; c++) order[c] = c;
    return { parent, cells: { coords: coords.slice(0, cells * 3), counts: counts.slice(0, cells), dimX, dimY, dimZ, order } };
}

/**
 * 26-connected components of occupied cells, with union-find: per cell, the
 * splats in its component. `neighbourhood` (pre-filled with each cell's own
 * count) also gets the splats of every neighbouring cell added.
 *
 * Neighbours are found by merging the sorted cell keys with themselves shifted
 * by each of the 13 forward offsets: sequential passes instead of 13 hash
 * lookups per cell, whose cache misses made the nine levels of a sparse
 * 3M-splat scan take ~2.5× as long. Each neighbouring pair is visited once
 * and counted for both cells.
 */
function* cellComponents(
    level: LevelCells, neighbourhood?: Uint32Array,
): Generator<void, { componentSize: Uint32Array; largest: number; clusters: number }, void> {
    const { coords, counts, order, dimX, dimY } = level;
    const cells = counts.length;
    const parent = new Int32Array(cells);
    const rank = new Uint8Array(cells);
    for (let c = 0; c < cells; c++) parent[c] = c;
    const find = (a: number): number => {
        let r = a;
        while (parent[r] !== r) r = parent[r];
        while (parent[a] !== r) { const next = parent[a]; parent[a] = r; a = next; }
        return r;
    };
    const union = (a: number, b: number) => {
        let ra = find(a), rb = find(b);
        if (ra === rb) return;
        if (rank[ra] < rank[rb]) { const t = ra; ra = rb; rb = t; }
        parent[rb] = ra;
        if (rank[ra] === rank[rb]) rank[ra]++;
    };
    const keys = new Float64Array(cells);
    for (let i = 0; i < cells; i++) {
        const c = order[i];
        keys[i] = (coords[c * 3 + 2] * dimY + coords[c * 3 + 1]) * dimX + coords[c * 3];
    }
    for (let k = 0; k < FORWARD.length; k += 3) {
        const dx = FORWARD[k], dy = FORWARD[k + 1];
        const offset = (FORWARD[k + 2] * dimY + dy) * dimX + dx;
        // keys + offset is sorted too, so one forward walk finds every match
        for (let i = 0, j = 0; i < cells; i++) {
            if (i % 100_000 === 99_999) yield;
            const want = keys[i] + offset;
            while (j < cells && keys[j] < want) j++;
            if (j === cells) break;
            if (keys[j] !== want) continue;
            const c = order[i];
            // a key past the edge of a row or slice wraps onto another cell
            const x = coords[c * 3] + dx, y = coords[c * 3 + 1] + dy;
            if (x < 0 || y < 0 || x >= dimX || y >= dimY) continue;
            const d = order[j];
            if (neighbourhood) {
                neighbourhood[c] += counts[d];
                neighbourhood[d] += counts[c];
            }
            union(c, d);
        }
    }
    const rootSize = new Uint32Array(cells);
    for (let c = 0; c < cells; c++) rootSize[find(c)] += counts[c];
    let largest = 0, clusters = 0;
    for (let c = 0; c < cells; c++) {
        if (parent[c] !== c) continue;
        clusters++;
        if (rootSize[c] > largest) largest = rootSize[c];
    }
    const componentSize = new Uint32Array(cells);
    for (let c = 0; c < cells; c++) componentSize[c] = rootSize[find(c)];
    return { componentSize, largest, clusters };
}

/**
 * Threshold a FloaterAnalysis: splats in small components (at the given
 * linking level) or sparse neighbourhoods. The size threshold is always a
 * fraction of the largest cluster at the base radius, so lowering the level
 * or raising either threshold never flags fewer splats.
 */
export function floaterMask(
    analysis: FloaterAnalysis, options: Pick<FloaterOptions, 'minClusterFraction' | 'minNeighbours' | 'level'> = {},
): { mask: Uint8Array; radius: number; clusters: number; count: number } {
    const { grid, componentSize, neighbourhood, levels } = analysis;
    const fraction = options.minClusterFraction ?? 0.01;
    const minNeighbours = options.minNeighbours ?? 2;
    const level = Math.max(0, Math.min(levels.length, Math.round(options.level ?? 0)));
    // component sizes at that level, handed down one level at a time to the grid's cells
    let sizes = level ? levels[level - 1].componentSize : componentSize;
    for (let k = level - 1; k >= 0; k--) {
        const { parent } = levels[k];
        const down = new Uint32Array(parent.length);
        for (let c = 0; c < parent.length; c++) down[c] = sizes[parent[c]];
        sizes = down;
    }
    const mask = new Uint8Array(analysis.numSplats);
    const threshold = Math.max(1, analysis.largest * fraction);
    let count = 0;
    for (let c = 0; c < componentSize.length; c++) {
        const floater = sizes[c] < threshold || neighbourhood[c] - 1 < minNeighbours;
        if (!floater) continue;
        for (let k = grid.starts[c]; k < grid.starts[c + 1]; k++) { mask[grid.items[k]] = 1; count++; }
    }
    return { mask, radius: analysis.radius, clusters: level ? levels[level - 1].clusters : analysis.clusters, count };
}
